<template lang="pug">
.briq-form-element.briq-input
  label.input-label.type-bold(:for="name" v-if="label") {{ label }} 
  briq-required-and-optional-markers(:isRequired="isRequired" :hideOptionalMarker="hideOptionalMarker" :label="label")
  BriqTooltip.ml-5(:help="help")
  .input-wrapper
    input.input.type-standard(
      :class="extraInputClasses"
      :name="name"
      :id="id"
      :value="innerValue"
      v-bind="$attrs"
      :disabled="isDisabled"
      v-on="{...$listeners, input: e => handleInput(e)}"
      :style= "prepend !== null ? `padding-left: ${prependWidth}` : ``"
      )
    .append-slot(v-if="!!$slots.append")
      slot(name="append")
    span.edit-input-link(v-if="showEditLink" @click="enableInput()") Edit
    span.prepend.type-standard(v-if="prepend") {{ prepend }}
  p.character-counter(:class="{ 'limit-reached': maxlengthReached }" v-show="showCharacterCounter") {{countLabel}}
  .validation-message(v-if="error" :data-test-id="`validation-message-${this.$attrs['data-test-id']}`") {{ error }}
</template>

<script>
import { ifElse, equals } from 'ramda'
import CharacterCountMixin from './CharacterCountMixin.js'

export default {
  inheritAttrs: false,
  props: {
    name: String, // this is "key" in text.blade.php
    id: String,
    label: String, // this is "name" in text.blade.php
    hideOptionalMarker: { type: Boolean, default: false },
    help: String,
    error: String,
    value: [String, Number],
    withCharacterCounter: Boolean,
    prepend: {
      type: String,
      default: null,
      validator: ifElse(
        equals(''),
        () => {
          console.error('prepend prop must not be an empty string')
          return false
        },
        () => true,
      ),
    },
    editInput: Boolean,
    required: [Boolean, String],
    hideSpinners: Boolean,
  },
  data() {
    return {
      isDisabled: false,
    }
  },
  beforeMount() {
    this.isDisabled = this.disabledProperty
  },
  watch: {
    disabledProperty(newValue) {
      this.isDisabled = this.disabledProperty
    },
  },
  computed: {
    activeName() {
      // prevent placeholder value from being submitted
      if (this.placeholderIsActive && this.allowInputFieldToBeSubmited === false) {
        return null
      }

      return this.name
    },
    disabledProperty() {
      return this.$attrs.disabled === '' || this.$attrs.disabled === true
    },
    prependWidth() {
      const prependWidth =
        this.prepend !== null ? this.prepend.length - this.prepend.length / 2 + 2.5 : 0
      return `${prependWidth}em`
    },
    isRequired() {
      return this.$attrs.required || this.required
    },
    showEditLink() {
      return this.editInput && this.isDisabled
    },
    extraInputClasses() {
      return this.hideSpinners ? 'hide-spinners' : ''
    },
  },
  methods: {
    enableInput() {
      this.isDisabled = false
    },
  },
  mixins: [CharacterCountMixin],
}
</script>

<style scoped lang="stylus">
.input-label
  margin-right 4px
  font-weight 500

.character-counter
  color #c1c1c1
  float right

.limit-reached
  color #ff7901

.briq-input
  width 100%
  position relative

input.input
  position relative
  width 100%
  border solid 1px #ebebeb
  border-radius 3px
  background white
  color #2c333a
  padding 7px

  &[disabled], &[readonly]
    background #eee

  &[disabled]
    cursor not-allowed

  &::placeholder
    color fade(@color-text, 40%)

  &:hover
    border-color #d3d3d3

  &:focus, &:active
    outline 0
    border-color #009BFF
    box-shadow 0 2px 4px 0 rgba(0, 0, 0, .15)


.hide-spinners::-webkit-outer-spin-button,
.hide-spinners::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.hide-spinners[type=number] {
  -moz-appearance: textfield;
}

.input-wrapper
  position relative
  display flex

  .append-slot
    margin-left 4px

.prepend
  position absolute
  display block
  top 50%
  transform translate(0, -50%)
  font-style normal
  border-right #ebebeb
  border-radius 1px 0px 0px 1px
  background #F1F3F6
  color #2c333a
  padding 7px 18px 7px 10px
  left 1px

  &:focus, &:active
    outline 0
    border-color #009BFF

.input-label
  display inline-block
  max-width 100%
  margin-bottom 4px

.edit-input-link
  position absolute
  right 16px
  top 12px
  text-decoration underline
  cursor pointer
</style>
